import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

export default function Pricing() {
  const { t } = useI18n();
  const tiers = t.pricing.tiers;

  const scrollToContact = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="pricing" className="relative py-24 sm:py-32" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="text-xs tracking-[0.4em] text-[#FF6B00] uppercase">{t.pricing.eyebrow}</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 uppercase">
            {t.pricing.title}
          </h2>
          <p className="mt-4 text-zinc-400 text-sm tracking-wide">{t.pricing.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {tiers.map((tier, i) => {
            const isPremium = tier.highlight;
            const inner = (
              <div
                className={`relative h-full glass-card p-8 lg:p-10 flex flex-col lift-card ${
                  isPremium ? "lg:py-12" : ""
                }`}
              >
                <div className="font-display text-3xl sm:text-4xl tracking-wide uppercase">
                  {tier.name}
                </div>
                <div className="text-sm text-zinc-400 mt-1">{tier.tagline}</div>

                <div className="mt-6 flex items-baseline gap-2">
                  <span className="text-zinc-500 text-2xl font-light">$</span>
                  <span className="font-display text-6xl sm:text-7xl">{tier.price}</span>
                  <span className="text-zinc-500 text-sm tracking-wide">{t.pricing.perMonth}</span>
                </div>

                <div className="mf-divider my-8" />

                <ul className="space-y-3 mb-10 grow">
                  {tier.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-3 text-sm text-zinc-300">
                      <Check
                        size={16}
                        className={`mt-1 shrink-0 ${isPremium ? "text-[#D4A853]" : "text-[#FF6B00]"}`}
                        strokeWidth={3}
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={scrollToContact}
                  className={`w-full py-4 text-xs tracking-[0.3em] font-bold uppercase transition-all ${
                    isPremium
                      ? "bg-[#FF6B00] text-black btn-glow"
                      : "border border-white/20 text-white hover:border-[#FF6B00] hover:text-[#FF6B00]"
                  }`}
                  data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
                >
                  {t.pricing.cta}
                </button>
              </div>
            );

            return (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className={`relative ${isPremium ? "lg:scale-105 lg:z-10" : ""}`}
                data-testid={`pricing-card-${tier.name.toLowerCase()}`}
              >
                {isPremium && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FF6B00] text-black text-[10px] font-bold tracking-[0.25em] px-4 py-1.5 uppercase z-20 whitespace-nowrap">
                    {t.pricing.popular}
                  </div>
                )}
                {isPremium ? <div className="premium-border-wrap p-[2px]">{inner}</div> : inner}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
